import {useState} from 'react'
import {Check, Edit, Settings, X} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {Card, CardContent, CardDescription, CardTitle} from '@/components/ui/card'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Switch} from '@/components/ui/switch'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {MutedText} from '@/components/shared/MutedText'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {Icon} from '@/components/shared/Icon'
import {CodeText} from '@/components/shared/CodeText'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {AuthType} from '@/types/outbound-connector'
import type {OutboundConnector, K8sOutboundConfigCreateRequest, K8sOutboundConfigUpdateRequest} from '@/types/outbound-connector'

interface K8sFormState {
    apiServerUrl: string
    authType: AuthType
    tokenRef: string
    caCertRef: string
    defaultNamespace: string
    skipTlsVerify: boolean
    connectTimeoutMs: number
    requestTimeoutMs: number
    version: number
}

const DEFAULT_K8S_FORM: K8sFormState = {
    apiServerUrl: '',
    authType: AuthType.TOKEN,
    tokenRef: '',
    caCertRef: '',
    defaultNamespace: '',
    skipTlsVerify: false,
    connectTimeoutMs: 5000,
    requestTimeoutMs: 30000,
    version: 0,
}

interface K8sConfigCardProps {
    connector: OutboundConnector
    canUpdate: boolean
}

export function K8sConfigCard({connector, canUpdate}: K8sConfigCardProps) {
    const services = useServices()
    const i18n = useI18n()
    const createDialog = useDialog()
    const editDialog = useDialog()
    const [form, setForm] = useState<K8sFormState>(DEFAULT_K8S_FORM)

    const updateForm = (updates: Partial<K8sFormState>) => setForm((prev) => ({...prev, ...updates}))

    const createMutation = useEntityMutation({
        mutationFn: (data: K8sOutboundConfigCreateRequest) =>
            services.outboundConnectors.createK8sConfig(connector.id, data),
        invalidate: ['connector', connector.id],
        successMessage: i18n.connectors.configurationSaved,
        closeDialog: createDialog.close,
    })

    const updateMutation = useEntityMutation({
        mutationFn: (data: K8sOutboundConfigUpdateRequest) =>
            services.outboundConnectors.updateK8sConfig(connector.id, data),
        invalidate: ['connector', connector.id],
        successMessage: i18n.connectors.configurationSaved,
        closeDialog: editDialog.close,
    })

    const openCreate = () => {
        setForm(DEFAULT_K8S_FORM)
        createDialog.open()
    }

    const openEdit = () => {
        const config = connector.k8sConfig
        if (!config) return
        setForm({
            apiServerUrl: config.apiServerUrl,
            authType: config.authType,
            tokenRef: config.tokenRef ?? '',
            caCertRef: config.caCertRef ?? '',
            defaultNamespace: config.defaultNamespace ?? '',
            skipTlsVerify: config.skipTlsVerify,
            connectTimeoutMs: config.connectTimeoutMs,
            requestTimeoutMs: config.requestTimeoutMs,
            version: config.version,
        })
        editDialog.open()
    }

    const handleCreate = () => {
        const request: K8sOutboundConfigCreateRequest = {
            apiServerUrl: form.apiServerUrl,
            authType: form.authType || undefined,
            tokenRef: form.tokenRef || undefined,
            caCertRef: form.caCertRef || undefined,
            defaultNamespace: form.defaultNamespace || undefined,
            skipTlsVerify: form.skipTlsVerify || undefined,
            connectTimeoutMs: form.connectTimeoutMs || undefined,
            requestTimeoutMs: form.requestTimeoutMs || undefined,
        }
        createMutation.mutate(request)
    }

    const handleUpdate = () => {
        const request: K8sOutboundConfigUpdateRequest = {
            apiServerUrl: form.apiServerUrl,
            authType: form.authType,
            tokenRef: form.tokenRef || null,
            caCertRef: form.caCertRef || null,
            defaultNamespace: form.defaultNamespace || null,
            skipTlsVerify: form.skipTlsVerify,
            connectTimeoutMs: form.connectTimeoutMs,
            requestTimeoutMs: form.requestTimeoutMs,
            version: form.version,
        }
        updateMutation.mutate(request)
    }

    const k8sConfig = connector.k8sConfig

    const authOptions = [
        {value: AuthType.TOKEN, label: i18n.connectors.tokenAuth},
        {value: AuthType.CERTIFICATE, label: i18n.connectors.certificateAuth},
        {value: AuthType.KUBECONFIG, label: i18n.connectors.kubeconfigAuth},
    ]

    const formFields = (mode: 'create' | 'edit') => {
        const handleSubmit = () => {
            if (mode === 'create') {
                handleCreate()
            } else {
                handleUpdate()
            }
        }
        return (
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit() }}>
            <div className="grid gap-4 py-4">
                <div>
                    <Label htmlFor="k8s-apiServerUrl">{i18n.connectors.apiServerUrl}</Label>
                    <Input id="k8s-apiServerUrl" value={form.apiServerUrl}
                        onChange={(e) => updateForm({apiServerUrl: e.target.value})}
                        placeholder={i18n.connectors.apiServerUrlPlaceholder}/>
                </div>
                <DetailGrid columns={2} responsive={false}>
                    <div>
                        <Label htmlFor="k8s-authType">{i18n.connectors.authType}</Label>
                        <Select value={form.authType} onValueChange={(v) => updateForm({authType: v as AuthType})}>
                            <SelectTrigger id="k8s-authType" data-testid="select-k8s-auth-type"><SelectValue/></SelectTrigger>
                            <SelectContent>
                                {authOptions.map(({value, label}) => (
                                    <SelectItem key={value} value={value}>{label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label htmlFor="k8s-defaultNamespace">{i18n.connectors.defaultNamespace}</Label>
                        <Input id="k8s-defaultNamespace" value={form.defaultNamespace}
                            onChange={(e) => updateForm({defaultNamespace: e.target.value})}
                            placeholder={i18n.connectors.defaultNamespacePlaceholder}/>
                    </div>
                </DetailGrid>
                <div>
                    <Label htmlFor="k8s-tokenRef">{i18n.connectors.tokenRef}</Label>
                    <Input id="k8s-tokenRef" value={form.tokenRef}
                        onChange={(e) => updateForm({tokenRef: e.target.value})}
                        placeholder={i18n.connectors.tokenRefPlaceholder}/>
                </div>
                <div>
                    <Label htmlFor="k8s-caCertRef">{i18n.connectors.caCertRef}</Label>
                    <Input id="k8s-caCertRef" value={form.caCertRef}
                        onChange={(e) => updateForm({caCertRef: e.target.value})}
                        placeholder={i18n.connectors.caCertRefPlaceholder}/>
                </div>
                <div className="items-center space-x-2">
                    <Switch id="k8s-skipTlsVerify" checked={form.skipTlsVerify}
                        onCheckedChange={(v) => updateForm({skipTlsVerify: v})}/>
                    <Label htmlFor="k8s-skipTlsVerify">{i18n.connectors.skipTlsVerify}</Label>
                </div>
                <DetailGrid columns={2} responsive={false}>
                    <div>
                        <Label htmlFor="k8s-connectTimeoutMs">{i18n.connectors.connectTimeout}</Label>
                        <Input id="k8s-connectTimeoutMs" type="number" value={form.connectTimeoutMs}
                            onChange={(e) => updateForm({connectTimeoutMs: parseInt(e.target.value) || 0})}/>
                    </div>
                    <div>
                        <Label htmlFor="k8s-requestTimeoutMs">{i18n.connectors.requestTimeoutMs}</Label>
                        <Input id="k8s-requestTimeoutMs" type="number" value={form.requestTimeoutMs}
                            onChange={(e) => updateForm({requestTimeoutMs: parseInt(e.target.value) || 0})}/>
                    </div>
                </DetailGrid>
            </div>
            <SaveCancelFooter
                testIdPrefix={mode === 'create' ? 'create-k8s' : 'edit-k8s'}
                onCancel={() => mode === 'create' ? createDialog.close() : editDialog.close()}
                saving={mode === 'create' ? createMutation.isPending : updateMutation.isPending}
                disabled={!form.apiServerUrl}
                saveLabel={mode === 'create' ? i18n.connectors.saveConfiguration : i18n.common.saveChanges}
            />
        </form>
        )
    }

    return (
        <>
            <Card data-testid="k8s-config-card">
                <RowCardHeader>
                    <div>
                        <CardTitle className="items-center gap-2" data-testid="card-title-k8s-config">
                            <Settings className="h-5 w-5"/>
                            {i18n.connectors.k8sConfiguration}
                        </CardTitle>
                        <CardDescription>{i18n.connectors.k8sDescription}</CardDescription>
                    </div>
                    {canUpdate && (
                        k8sConfig ? (
                            <Button data-testid="btn-edit-k8s-config" size="sm" onClick={openEdit}>
                                <Icon icon={Edit} className="mr-2"/>
                                {i18n.common.edit}
                            </Button>
                        ) : (
                            <Button data-testid="btn-create-k8s-config" size="sm" onClick={openCreate}>
                                <Icon icon={Settings} className="mr-2"/>
                                {i18n.connectors.configureK8sConnection}
                            </Button>
                        )
                    )}
                </RowCardHeader>
                <CardContent>
                    {k8sConfig ? (
                        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.apiServerUrl}</dt>
                                <dd><CodeText>{k8sConfig.apiServerUrl}</CodeText></dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.authType}</dt>
                                <dd><CodeText>{k8sConfig.authType}</CodeText></dd>
                            </div>
                            {k8sConfig.tokenRef && (
                                <div>
                                    <dt className="font-medium text-muted-foreground">{i18n.connectors.tokenRef}</dt>
                                    <dd><CodeText>{k8sConfig.tokenRef}</CodeText></dd>
                                </div>
                            )}
                            {k8sConfig.defaultNamespace && (
                                <div>
                                    <dt className="font-medium text-muted-foreground">{i18n.connectors.defaultNamespace}</dt>
                                    <dd><CodeText>{k8sConfig.defaultNamespace}</CodeText></dd>
                                </div>
                            )}
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.skipTlsVerify}</dt>
                                <dd className="mt-1">
                                    {k8sConfig.skipTlsVerify
                                        ? <Check className="h-4 w-4 text-status-success"/>
                                        : <Icon icon={X} muted/>}
                                </dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.connectTimeout}</dt>
                                <dd className="mt-1">{i18n.common.valueWithMs({value: k8sConfig.connectTimeoutMs})}</dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.requestTimeoutMs}</dt>
                                <dd className="mt-1">{i18n.common.valueWithMs({value: k8sConfig.requestTimeoutMs})}</dd>
                            </div>
                        </dl>
                    ) : (
                        <MutedText block centered className="py-4">{i18n.connectors.k8sNotConfigured}</MutedText>
                    )}
                </CardContent>
            </Card>

            <Dialog open={createDialog.isOpen} onOpenChange={createDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.connectors.configureK8sConnection}</DialogTitle>
                        <DialogDescription>{i18n.connectors.configureK8sDescription}</DialogDescription>
                    </DialogHeader>
                    {formFields('create')}
                </DialogContent>
            </Dialog>

            <Dialog open={editDialog.isOpen} onOpenChange={editDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.connectors.editK8sConfiguration}</DialogTitle>
                        <DialogDescription>{i18n.connectors.editK8sDescription}</DialogDescription>
                    </DialogHeader>
                    {formFields('edit')}
                </DialogContent>
            </Dialog>
        </>
    )
}
